import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Download, LogOut, Heart, LifeBuoy, ShieldCheck, Bell, Info, FileText,
  Pencil, KeyRound, Laptop, RotateCcw, Trash2, CreditCard, Sparkles, Lock, ChevronRight, Users, Languages, Ticket, Cpu, UserPlus, LineChart,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PromoRedeem } from "@/components/PromoRedeem";
import InstallAppButton from "@/components/InstallAppButton";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DICTATION_LANGS, getDictationLang, setDictationLang } from "@/hooks/useDictation";

function Hint({ text }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label="What is this?" data-testid="settings-hint"
          className="ml-1 inline-flex text-[#8A838E] hover:text-[#291B2D] dark:hover:text-white transition-smooth">
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[240px] text-xs leading-relaxed">{text}</TooltipContent>
    </Tooltip>
  );
}

function SectionLabel({ children }) {
  return <p className="text-xs uppercase tracking-widest text-[#8A838E] font-semibold mb-3 px-1">{children}</p>;
}

function Card({ children, className = "" }) {
  return (
    <div className={`bg-white border border-[#E5DED4] rounded-2xl dark:bg-[#352238] dark:border-[#47344B] ${className}`}>
      {children}
    </div>
  );
}

function Toggle({ on, onClick, testid, label }) {
  return (
    <button onClick={onClick} data-testid={testid} aria-label={label}
      className={`h-6 w-11 rounded-full flex items-center px-0.5 transition-all shrink-0 ${on ? "bg-[#C89B55]" : "bg-[#D6CFC7] dark:bg-[#5A4560]"}`}>
      <span className={`h-5 w-5 rounded-full bg-white transition-all ${on ? "translate-x-5" : ""}`} />
    </button>
  );
}

export default function SettingsPage() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [faith, setFaith] = useState(!!user?.faith_enabled);
  const [reminders, setReminders] = useState(() => localStorage.getItem("sh_reminders") !== "off");
  const [dictLang, setDictLang] = useState(() => getDictationLang());

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [emailReflections, setEmailReflections] = useState(user?.email_reflections !== false);
  const [emailCommunity, setEmailCommunity] = useState(user?.email_community !== false);
  const [emailProduct, setEmailProduct] = useState(user?.email_product !== false);

  const [sessions, setSessions] = useState([]);
  const [sub, setSub] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => { document.documentElement.classList.remove("dark"); }, []);
  useEffect(() => { api.get("/auth/sessions").then((r) => setSessions(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/billing/status").then((r) => setSub(r.data)).catch(() => {}); }, []);

  const sendPreview = async (type) => {
    try {
      const { data } = await api.post("/emails/preview", { type });
      toast.success(`Preview sent to ${data.sent_to}`);
    } catch {
      toast.error("Couldn't send that preview. Please try again.");
    }
  };

  const toggleFaith = async () => {
    const next = !faith;
    setFaith(next);
    const { data } = await api.put("/profile", { faith_enabled: next });
    setUser(data);
    toast.success(next ? "Faith & spirituality enabled." : "Faith & spirituality turned off.");
  };

  const toggleReminders = () => {
    const next = !reminders;
    setReminders(next);
    localStorage.setItem("sh_reminders", next ? "on" : "off");
    toast.success(next ? "Gentle reminders on." : "Reminders paused.");
  };

  const saveName = async () => {
    const clean = nameDraft.trim();
    if (!clean) return;
    setSavingName(true);
    try {
      const { data } = await api.put("/profile", { name: clean });
      setUser(data);
      setEditingName(false);
      toast.success("Name updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSavingName(false); }
  };

  const setEmailPref = async (key, val, setter) => {
    setter(val);
    try {
      const { data } = await api.put("/profile", { [key]: val });
      setUser(data);
    } catch { toast.error("Could not save preference."); }
  };

  const changePassword = async () => {
    const current = window.prompt("Enter your current password");
    if (!current) return;
    const next = window.prompt("Enter a new password (8+ characters)");
    if (!next) return;
    if (next.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Other devices have been signed out.");
      api.get("/auth/sessions").then((r) => setSessions(r.data || [])).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const revokeOthers = async () => {
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(data?.revoked ? `Signed out ${data.revoked} other device(s).` : "No other devices to sign out.");
      api.get("/auth/sessions").then((r) => setSessions(r.data || [])).catch(() => {});
    } catch { toast.error("Could not sign out other devices."); }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel your Sisterhood+ membership? You'll keep full access until the end of your paid period.")) return;
    setCancelling(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Membership cancelled. You'll keep access until your period ends.");
      const r = await api.get("/billing/status");
      setSub(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setCancelling(false); }
  };

  const replayTour = () => {
    localStorage.removeItem("sh_tour_completed");
    window.dispatchEvent(new Event("sh:replay-tour"));
    navigate("/home");
    toast.success("Guided tour restarted.");
  };

  const exportData = async () => {
    const [me, journal, wellness] = await Promise.all([
      api.get("/auth/me"), api.get("/journal"), api.get("/wellness/trends"),
    ]);
    const blob = new Blob([JSON.stringify({ profile: me.data, journal: journal.data, wellness: wellness.data }, null, 2)],
      { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `sisterhood-plus-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Your data has been exported.");
  };

  const deleteAccount = async () => {
    if (!window.confirm("Permanently delete your account and all your data? This cannot be undone.")) return;
    try {
      await api.delete("/auth/account");
      await logout();
      navigate("/");
    } catch { toast.error("Could not delete your account. Please try again."); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="max-w-2xl mx-auto space-y-8" data-testid="settings-page">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Settings</p>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-1">Your account</h1>
        </div>

        {/* Identity card */}
        <div className="bg-[#291B2D] text-white rounded-2xl p-6 lg:p-8 dark:border dark:border-[#47344B]">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-full bg-[#C89B55] text-[#291B2D] flex items-center justify-center font-semibold text-xl overflow-hidden shrink-0">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name || "S").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              {editingName ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <input data-testid="settings-name-input" value={nameDraft} maxLength={60} autoFocus
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="flex-1 bg-white/10 border border-white/25 rounded-xl px-3 py-2 text-white text-lg font-display focus:outline-none focus:border-[#C89B55]"
                    placeholder="Your name" />
                  <div className="flex gap-2">
                    <button data-testid="settings-name-save" onClick={saveName} disabled={savingName || !nameDraft.trim()}
                      className="bg-[#C89B55] text-[#291B2D] rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50">
                      {savingName ? "Saving…" : "Save"}
                    </button>
                    <button data-testid="settings-name-cancel" onClick={() => setEditingName(false)}
                      className="text-white/60 hover:text-white px-2 py-2 text-sm">Cancel</button>
                  </div>
                </div>
              ) : (
                <button data-testid="settings-name-edit" onClick={() => { setNameDraft(user?.name || ""); setEditingName(true); }}
                  className="group flex items-center gap-2.5 text-left">
                  <span className="font-display text-xl lg:text-2xl font-semibold truncate">{user?.name}</span>
                  <Pencil size={16} className="text-white/40 group-hover:text-[#C89B55] transition-colors shrink-0" />
                </button>
              )}
              <p className="text-white/60 mt-0.5 truncate">{user?.email}</p>
            </div>
            {user?.role === "owner" && <span className="bg-[#C89B55] text-[#291B2D] text-xs font-bold px-3 py-1 rounded-full shrink-0">Admin</span>}
            {user?.is_premium && user?.role !== "owner" && <span className="bg-[#C89B55] text-[#291B2D] text-xs font-bold px-3 py-1 rounded-full shrink-0">Member</span>}
          </div>
        </div>

        {/* Account */}
        <div>
          <SectionLabel>Account</SectionLabel>
          <Card className="divide-y divide-[#E5DED4] dark:divide-[#47344B]">
            <Link to="/terms" data-testid="settings-terms" className="flex items-center justify-between p-6">
              <span className="flex items-center gap-3"><FileText className="h-5 w-5 text-[#C89B55]" /> Legal &amp; privacy · Terms of Service</span>
              <ChevronRight className="h-4 w-4 text-[#8A838E]" />
            </Link>
            {user?.role === "owner" && (
              <Link to="/admin/users" data-testid="settings-user-management" className="flex items-center justify-between p-6">
                <span className="flex items-center gap-3"><Users className="h-5 w-5 text-[#C89B55]" /> User management</span>
                <ChevronRight className="h-4 w-4 text-[#8A838E]" />
              </Link>
            )}
            {user?.role === "owner" && (
              <Link to="/admin/promo-codes" data-testid="settings-promo-codes" className="flex items-center justify-between p-6">
                <span className="flex items-center gap-3"><Ticket className="h-5 w-5 text-[#C89B55]" /> Promo codes</span>
                <ChevronRight className="h-4 w-4 text-[#8A838E]" />
              </Link>
            )}
            {user?.role === "owner" && (
              <Link to="/admin/member-insights" data-testid="settings-member-insights" className="flex items-center justify-between p-6">
                <span className="flex items-center gap-3"><LineChart className="h-5 w-5 text-[#C89B55]" /> Member insights</span>
                <ChevronRight className="h-4 w-4 text-[#8A838E]" />
              </Link>
            )}
            {user?.role === "owner" && (
              <Link to="/admin/ai-usage" data-testid="settings-ai-usage" className="flex items-center justify-between p-6">
                <span className="flex items-center gap-3"><Cpu className="h-5 w-5 text-[#C89B55]" /> AI usage</span>
                <ChevronRight className="h-4 w-4 text-[#8A838E]" />
              </Link>
            )}
            {user?.role === "owner" && (
              <Link to="/admin/therapists" data-testid="settings-therapists" className="flex items-center justify-between p-6">
                <span className="flex items-center gap-3"><UserPlus className="h-5 w-5 text-[#C89B55]" /> Therapists</span>
                <ChevronRight className="h-4 w-4 text-[#8A838E]" />
              </Link>
            )}
            {user?.role === "owner" && (
              <Link to="/moderation" data-testid="settings-moderation" className="flex items-center justify-between p-6">
                <span className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[#C89B55]" /> Moderation dashboard</span>
                <ChevronRight className="h-4 w-4 text-[#8A838E]" />
              </Link>
            )}
          </Card>
        </div>

        {/* Membership */}
        <div>
          <SectionLabel>Membership</SectionLabel>
          <Card className="p-6 space-y-4" >
            {(() => {
              const isOwner = user?.role === "owner";
              const premium = isOwner || sub?.is_premium || user?.is_premium;
              const trialing = !isOwner && !premium && user?.is_trial_active;
              const status = sub?.subscription?.status;
              const planLabel = sub?.subscription?.plan === "annual" ? "Annual · $99.99/yr" : sub?.subscription?.plan === "monthly" ? "Monthly · $14.99/mo" : null;
              const cancelled = status === "CANCELLED";
              const nextBilling = sub?.subscription?.next_billing_time;
              const fmt = (iso) => { if (!iso) return null; const d = new Date(iso); return isNaN(d) ? null : d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); };
              return (
                <>
                  <div className="flex items-start gap-3" data-testid="settings-membership">
                    <CreditCard className="h-5 w-5 text-[#C89B55] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Sisterhood+ membership</p>
                      <p className="font-display text-lg mt-0.5">
                        {isOwner ? "Owner access" : premium ? (cancelled ? "Active — cancels at period end" : "Active member") : trialing ? "Free trial" : "No active membership"}
                      </p>
                      {!isOwner && premium && planLabel && (
                        <p className="text-sm text-[#59535B] dark:text-[#A298A6] mt-1">{planLabel}</p>
                      )}
                      <p className="text-sm text-[#59535B] dark:text-[#A298A6] mt-1 leading-relaxed">
                        {isOwner
                          ? "You have full owner access to everything in Sisterhood+."
                          : premium
                            ? (cancelled
                                ? `Your membership won't renew${nextBilling ? `. Access continues until ${fmt(nextBilling)}.` : "."}`
                                : `Full access to Sisterhood University, the AI Coach, and community.${nextBilling ? ` Renews ${fmt(nextBilling)}.` : ""}`)
                            : trialing
                              ? `${user?.access?.trial_days_left ?? 0} day${(user?.access?.trial_days_left ?? 0) === 1 ? "" : "s"} left · ${user?.access?.trial_ai_used ?? 0} of ${user?.access?.trial_ai_limit ?? 5} free AI sessions used. Subscribe to unlock unlimited AI.`
                              : "Unlock everything for $14.99/month or $99.99/year — cancel anytime."}
                      </p>
                    </div>
                  </div>
                  {!isOwner && !premium && (
                    <button data-testid="settings-upgrade" onClick={() => navigate("/paywall")}
                      className="w-full rounded-full bg-[#291B2D] text-white hover:bg-[#291B2D]/90 px-6 py-3 text-sm font-medium dark:bg-[#C89B55] dark:text-[#291B2D]">
                      {trialing ? "Subscribe now" : "View membership"}
                    </button>
                  )}
                  {!isOwner && !premium && (
                    <PromoRedeem />
                  )}
                  {!isOwner && premium && !cancelled && sub?.subscription?.id && (
                    <button data-testid="settings-cancel-subscription" onClick={cancelSubscription} disabled={cancelling}
                      className="w-full rounded-full border border-[#E5DED4] text-[#59535B] hover:bg-[#F7F2EA] px-6 py-3 text-sm font-medium disabled:opacity-50">
                      {cancelling ? "Cancelling…" : "Cancel membership"}
                    </button>
                  )}
                  {!isOwner && cancelled && (
                    <button data-testid="settings-resubscribe" onClick={() => navigate("/paywall")}
                      className="w-full rounded-full bg-[#291B2D] text-white hover:bg-[#291B2D]/90 px-6 py-3 text-sm font-medium dark:bg-[#C89B55] dark:text-[#291B2D]">
                      Renew membership
                    </button>
                  )}
                </>
              );
            })()}
          </Card>
        </div>

        {/* Install app */}
        <div>
          <SectionLabel>Install app</SectionLabel>
          <Card>
            <InstallAppButton />
          </Card>
        </div>

        {/* Security */}
        <div>
          <SectionLabel>Security</SectionLabel>
          <Card className="divide-y divide-[#E5DED4] dark:divide-[#47344B]">
            <div className="w-full flex items-center justify-between p-6" data-testid="settings-sessions">
              <span className="flex items-center gap-3"><Laptop className="h-5 w-5 text-[#C89B55]" /> Active sessions
                <Hint text="Every device where you're signed in. Sign out the others if a device was lost or shared." />
              </span>
              <span className="text-xs text-[#8A838E]">{sessions.length || 1} device{sessions.length === 1 ? "" : "s"}</span>
            </div>
            <button onClick={revokeOthers} data-testid="settings-revoke-others"
              className="w-full flex items-center justify-between p-6 text-left hover:bg-[#F7F2EA] dark:hover:bg-[#47344B]/40 transition-smooth">
              <span className="flex items-center gap-3"><LogOut className="h-5 w-5" /> Sign out other devices</span>
              <ChevronRight className="h-4 w-4 text-[#8A838E]" />
            </button>
            <button onClick={changePassword} data-testid="settings-change-password"
              className="w-full flex items-center justify-between p-6 text-left hover:bg-[#F7F2EA] dark:hover:bg-[#47344B]/40 transition-smooth">
              <span className="flex items-center gap-3"><KeyRound className="h-5 w-5 text-[#C89B55]" /> Change password</span>
              <ChevronRight className="h-4 w-4 text-[#8A838E]" />
            </button>
            <div className="w-full flex items-center justify-between p-6" data-testid="settings-2fa">
              <span className="flex items-center gap-3"><Lock className="h-5 w-5 text-[#C89B55]" /> Two-factor authentication
                <Hint text="Add a one-time code from your phone at sign-in for extra protection. Coming soon to Sisterhood+." />
              </span>
              <span className="text-xs text-[#8A838E]">Coming soon</span>
            </div>
            <div className="w-full flex items-center justify-between p-6" data-testid="settings-trusted-devices">
              <span className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[#C89B55]" /> Trusted devices
                <Hint text="Skip 2FA on devices you trust. Available once two-factor authentication launches." />
              </span>
              <span className="text-xs text-[#8A838E]">Coming soon</span>
            </div>
          </Card>
        </div>

        {/* Preferences */}
        <div>
          <SectionLabel>Preferences</SectionLabel>
          <Card className="divide-y divide-[#E5DED4] dark:divide-[#47344B]">
            <div className="w-full flex items-center justify-between p-6">
              <span className="flex items-center gap-3"><Bell className="h-5 w-5" /> Gentle reminders
                <Hint text="Soft nudges to check in, reflect, or read your daily affirmation. We never spam — turn these off anytime." />
              </span>
              <Toggle on={reminders} onClick={toggleReminders} testid="settings-reminders-toggle" label="Toggle reminders" />
            </div>
            <div className="w-full flex items-center justify-between p-6">
              <span className="flex items-center gap-3"><Heart className="h-5 w-5" /> Faith &amp; spirituality content
                <Hint text="Turn on an optional set of faith-based lessons and reflections. Off by default; it never changes the rest of the app." />
              </span>
              <Toggle on={faith} onClick={toggleFaith} testid="settings-faith-toggle" label="Toggle faith content" />
            </div>
            <div className="w-full flex items-center justify-between p-6">
              <span className="flex items-center gap-3"><Languages className="h-5 w-5" /> Voice typing language
                <Hint text="The language your speech is transcribed in when you tap the mic to speak in your journal, the Coach, community, and check-ins." />
              </span>
              <Select value={dictLang} onValueChange={(v) => { setDictLang(v); setDictationLang(v); toast.success("Voice typing language updated."); }}>
                <SelectTrigger data-testid="settings-dictation-lang" className="w-44 shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {DICTATION_LANGS.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`dictation-lang-${l.code}`}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button onClick={replayTour} data-testid="settings-replay-tour"
              className="w-full flex items-center justify-between p-6 text-left hover:bg-[#F7F2EA] dark:hover:bg-[#47344B]/40 transition-smooth">
              <span className="flex items-center gap-3"><RotateCcw className="h-5 w-5" /> Replay guided tour</span>
              <span className="text-xs text-[#8A838E]">Restart</span>
            </button>
          </Card>
        </div>

        {/* Email notifications */}
        <div>
          <SectionLabel>Email notifications</SectionLabel>
          <Card className="divide-y divide-[#E5DED4] dark:divide-[#47344B]">
            <div className="w-full flex items-center justify-between p-6">
              <span className="flex items-center gap-3"><Sparkles className="h-5 w-5 text-[#C89B55]" /> Daily affirmations &amp; reflections
                <Hint text="A gentle morning email with your affirmation and a reflection prompt." />
              </span>
              <Toggle on={emailReflections} onClick={() => setEmailPref("email_reflections", !emailReflections, setEmailReflections)} testid="settings-email-reflections" label="Toggle reflection emails" />
            </div>
            <div className="w-full flex items-center justify-between p-6">
              <span className="flex items-center gap-3"><Heart className="h-5 w-5 text-[#C89B55]" /> Community replies &amp; reactions
                <Hint text="Get notified when a sister replies to or reacts to your community posts." />
              </span>
              <Toggle on={emailCommunity} onClick={() => setEmailPref("email_community", !emailCommunity, setEmailCommunity)} testid="settings-email-community" label="Toggle community emails" />
            </div>
            <div className="w-full flex items-center justify-between p-6">
              <span className="flex items-center gap-3"><Bell className="h-5 w-5 text-[#C89B55]" /> Product updates
                <Hint text="Occasional news about new lessons and features. Never more than once a month." />
              </span>
              <Toggle on={emailProduct} onClick={() => setEmailPref("email_product", !emailProduct, setEmailProduct)} testid="settings-email-product" label="Toggle product emails" />
            </div>
          </Card>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="settings-email-preview">
            <span className="text-xs text-[#8A838E] mr-1">Send yourself a preview:</span>
            {[["welcome", "Welcome"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]].map(([t, l]) => (
              <button key={t} data-testid={`settings-preview-${t}`} onClick={() => sendPreview(t)}
                className="text-xs rounded-full border border-[#E5DED4] px-3 py-1.5 hover:bg-[#F7F2EA] transition-smooth">{l}</button>
            ))}
          </div>
          {user?.role === "owner" && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="settings-billing-email-preview">
              <span className="text-xs text-[#8A838E] mr-1">Billing emails:</span>
              {[["sub_confirmed", "Confirmed"], ["renewal_receipt", "Renewal receipt"], ["renewal_reminder", "Renewal reminder"], ["payment_failed", "Payment failed"], ["cancellation", "Cancellation"]].map(([t, l]) => (
                <button key={t} data-testid={`settings-preview-${t}`} onClick={() => sendPreview(t)}
                  className="text-xs rounded-full border border-[#E5DED4] px-3 py-1.5 hover:bg-[#F7F2EA] transition-smooth">{l}</button>
              ))}
            </div>
          )}
        </div>

        {/* Privacy & data */}
        <div>
          <SectionLabel>Privacy &amp; data</SectionLabel>
          <Card className="divide-y divide-[#E5DED4] dark:divide-[#47344B]">
            <div className="w-full flex items-center gap-3 p-6">
              <button onClick={exportData} data-testid="settings-export-button" className="flex items-center gap-3 text-left hover:opacity-80 transition-smooth">
                <Download className="h-5 w-5" /> Export my data
              </button>
              <Hint text="Download a private copy of your profile, journal entries, and wellness history as a file you keep." />
            </div>
            <div className="w-full flex items-center gap-3 p-6">
              <ShieldCheck className="h-5 w-5" /> Your privacy
              <Hint text="Your journal and coaching conversations are private and are never used for analytics or sold to anyone." />
            </div>
            <button onClick={deleteAccount} data-testid="settings-delete-account"
              className="w-full flex items-center justify-between p-6 text-left text-[#9E3A3A] dark:text-[#E9A6A6] hover:bg-[#FBF1F1] dark:hover:bg-[#47344B]/40 transition-smooth">
              <span className="flex items-center gap-3"><Trash2 className="h-5 w-5" /> Delete my account</span>
              <ChevronRight className="h-4 w-4 opacity-60" />
            </button>
          </Card>
        </div>

        {/* Support & crisis */}
        <div>
          <SectionLabel>Support</SectionLabel>
          <Card className="divide-y divide-[#E5DED4] dark:divide-[#47344B]">
            <div className="p-6" data-testid="settings-crisis">
              <div className="flex items-center gap-3 font-medium text-[#9E3A3A] dark:text-[#E9A6A6]">
                <LifeBuoy className="h-5 w-5" /> In crisis or unsafe?
              </div>
              <p className="text-sm text-[#59535B] dark:text-[#A298A6] mt-2 leading-relaxed">
                Sisterhood+ is a growth and wellness space — it is <strong>not</strong> therapy, medical care, or a crisis service.
                If you are in danger or thinking about harming yourself, please reach out now: call or text{" "}
                <a href="tel:988" className="text-[#291B2D] dark:text-white underline font-medium">988</a> (Suicide &amp; Crisis Lifeline),
                or in an emergency call <a href="tel:911" className="text-[#291B2D] dark:text-white underline font-medium">911</a>.
              </p>
            </div>
            <div className="w-full flex items-start gap-3 p-6 text-sm text-[#59535B] dark:text-[#A298A6]">
              <FileText className="h-5 w-5 shrink-0" />
              <span>By using Sisterhood+ and the AI Coach, you've agreed to our terms of understanding: the coach offers supportive guidance and reflection, not professional diagnosis or treatment.</span>
            </div>
          </Card>
        </div>

        <button data-testid="settings-logout-button" onClick={handleLogout}
          className="w-full rounded-full border border-[#E5DED4] hover:bg-[#F7F2EA] px-7 py-3.5 font-medium flex items-center justify-center gap-2 dark:border-[#47344B] dark:hover:bg-[#47344B]/40">
          <LogOut className="h-4 w-4" /> Log out
        </button>

        <p className="text-center text-xs text-[#8A838E] leading-relaxed">
          © 2026 Renewing Minds Counseling and Consulting Services. All rights reserved.<br />
          Sisterhood+ · Your journal and coaching conversations are private and never used for analytics.
        </p>
      </div>
    </TooltipProvider>
  );
}
